import React from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Users, Award, Globe, Mail, Send, ArrowRight, Crown, Sparkles,
  ChevronRight, Zap, Lock, HeartHandshake,
} from 'lucide-react';
import { useSiteConfig } from '../contexts/SiteConfigContext';

/**
 * /info — About us / mission page (Iter-37 redesign).
 *
 * Rebuilt to match the /info/team page aesthetic — cinematic hero with
 * ambient blobs + grain + scanlines + prism accents, multi-color feature
 * tiles, and premium contact card. The "meet the team" callout survives
 * (routes to /info/team). No blog rambles — every section earns its space.
 */

const BADGES = [
  { Icon: Shield, label: 'Trusted Source',   desc: '9+ yrs · zero-log ops',      color: '#00ff9d' },
  { Icon: Users,  label: 'Community First',  desc: '12,400+ active operators',   color: '#ff2d92' },
  { Icon: Award,  label: 'Award Winning',    desc: 'Recognised methodology',     color: '#4de0ff' },
  { Icon: Globe,  label: 'Global Reach',     desc: '42 countries · 24/7 uptime', color: '#ffd34d' },
];

const PILLARS = [
  { Icon: Lock,          title: 'Encryption first',  body: 'Every operation runs through AES-256 · zero-log tunnels. We never store more than we absolutely need.', color: '#00ff9d' },
  { Icon: Zap,           title: 'Manual · never bot', body: 'Real operators, not scripts. Every case gets human eyes and a manual double-check before delivery.',   color: '#4de0ff' },
  { Icon: HeartHandshake, title: 'Pay on delivery',  body: 'You only pay when the op is done. If we can\'t recover it, you don\'t pay. Simple as that.',              color: '#ff2d92' },
];

const InfoPage = () => {
  const { config } = useSiteConfig();
  const SITE = config.site;
  return (
    <div className="info-page relative pb-20" data-testid="info-page">
      {/* ============ CINEMATIC HERO ============ */}
      <section className="info-hero relative overflow-hidden">
        <div aria-hidden className="blob b1" />
        <div aria-hidden className="blob b2" />
        <div aria-hidden className="blob b3" />
        <div aria-hidden className="grain" />
        <div aria-hidden className="scanlines" />
        <div aria-hidden className="grid-dots" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-14 sm:pb-20 text-center">
          <div className="eh-kicker justify-center mb-4">// WHO_WE_ARE · SINCE 2017</div>
          <h1 className="info-title font-black leading-[0.94]" data-testid="info-title">
            <span>the </span><span className="eh-neon">underground</span>
            <br className="hidden sm:block" />
            <span> · </span><span>done right.</span>
          </h1>
          <p className="mt-5 max-w-2xl mx-auto text-sm sm:text-base opacity-80 leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
            {SITE.description || 'A collective of ethical hackers, automation engineers and cybersecurity researchers demystifying the underground and equipping the next generation of operators.'}
          </p>

          {/* Fast CTAs */}
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link to="/info/team" data-testid="info-hero-team" className="cta-btn" style={{ '--c': '#00ff9d' }}>
              <Crown size={14} /> MEET THE TEAM
            </Link>
            <Link to="/tools" data-testid="info-hero-services" className="cta-btn ghost" style={{ '--c': '#4de0ff' }}>
              <Sparkles size={14} /> AI TOOLS <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ BADGE STRIP (multi-color) ============ */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 -mt-6 sm:-mt-10 relative z-10 mb-14 sm:mb-20" data-testid="info-badges">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {BADGES.map((b) => {
            const Icon = b.Icon;
            return (
              <div
                key={b.label}
                className="info-badge"
                style={{ '--c': b.color }}
              >
                <span aria-hidden className="prism-bar" />
                <div className="badge-icon">
                  <Icon size={20} strokeWidth={1.7} />
                </div>
                <div className="badge-label">{b.label}</div>
                <div className="badge-desc eh-mono">{b.desc}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ============ MANIFESTO / PILLARS ============ */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-14 sm:mb-20" data-testid="info-pillars">
        <div className="text-center mb-9 sm:mb-12">
          <div className="eh-kicker justify-center mb-3">// MANIFESTO</div>
          <h2 className="eh-display text-3xl sm:text-5xl font-black leading-tight">
            <span>Three rules </span><span className="eh-neon">we never break</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4 sm:gap-5">
          {PILLARS.map((p) => {
            const Icon = p.Icon;
            return (
              <div key={p.title} className="pillar-tile" style={{ '--c': p.color }}>
                <span aria-hidden className="prism-bar" />
                <span aria-hidden className="corner-glow" />
                <div className="pillar-icon"><Icon size={22} strokeWidth={1.7} /></div>
                <h3 className="pillar-title">{p.title}</h3>
                <p className="pillar-body">{p.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ============ MEET THE TEAM CTA (routes to /info/team) ============ */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-14 sm:mb-20">
        <Link
          to="/info/team"
          data-testid="info-meet-team"
          className="meet-team-card group"
        >
          <span aria-hidden className="prism-bar-rainbow" />
          <span aria-hidden className="pulse-dot">
            <span className="pulse-ping" />
            <span className="pulse-core" />
          </span>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <div className="team-avatar-tile">
              <Crown size={26} strokeWidth={1.7} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="eh-mono text-[10px] tracking-[0.25em] mb-1" style={{ color: 'var(--eh-green)' }}>// MEET THE TEAM</div>
              <div className="font-bold text-lg sm:text-2xl leading-tight" style={{ fontFamily: 'Inter,sans-serif' }}>
                The founder + 6 operatives behind every op
              </div>
              <div className="text-xs sm:text-sm opacity-70 mt-1.5" style={{ fontFamily: 'Inter,sans-serif' }}>
                Real people. Real callsigns. Real ops. Tap in to see who&apos;s watching your back.
              </div>
            </div>
            <div className="team-cta">MEET THE CREW <ArrowRight size={12} /></div>
          </div>
        </Link>
      </section>

      {/* ============ STATS GRID ============ */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-14 sm:mb-20" data-testid="info-stats">
        <div className="text-center mb-8">
          <div className="eh-kicker justify-center mb-2">// PROOF · IN NUMBERS</div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {(config.stats || []).map((s, i) => {
            const colors = ['#00ff9d', '#ff2d92', '#4de0ff', '#ffd34d'];
            const c = colors[i % colors.length];
            return (
              <div key={s.label} className="stat-cell" style={{ '--c': c }}>
                <div className="stat-value" style={{ color: c }}>{s.value}</div>
                <div className="stat-label eh-mono">{s.label}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ============ CONTACT CARD ============ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6" data-testid="info-contact">
        <div className="contact-card">
          <span aria-hidden className="prism-bar-rainbow" />
          <div className="eh-kicker justify-center mb-3">// REACH_OUT</div>
          <h2 className="eh-display text-2xl sm:text-4xl font-black mb-3 text-center">
            NEED A <span className="eh-neon">CUSTOM OP?</span>
          </h2>
          <p className="opacity-70 text-sm mb-6 text-center" style={{ fontFamily: 'Inter,sans-serif' }}>
            The founder personally reviews every request within 12 hours. No middleman, no phone-tree, no ticket queue.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href={SITE.telegram} target="_blank" rel="noreferrer" className="eh-btn-primary" data-testid="info-contact-telegram">
              <Send size={16} /> DM ON TELEGRAM
            </a>
            <a href={`mailto:${SITE.email}`} className="eh-btn-ghost" data-testid="info-contact-email">
              <Mail size={14} /> EMAIL DIRECT
            </a>
            <Link to="/tools" className="eh-btn-ghost" data-testid="info-contact-services">
              <ChevronRight size={12} className="rotate-180" /> AI TOOLS
            </Link>
          </div>
        </div>
      </section>

      {/* ================= SCOPED STYLES ================= */}
      <style>{`
        .info-page { --bg: #05060a; }

        /* HERO */
        .info-hero {
          position: relative;
          background: radial-gradient(ellipse at 30% -10%, rgba(0,255,157,.10), transparent 60%),
                      radial-gradient(ellipse at 100% 30%, rgba(255,45,146,.09), transparent 55%),
                      radial-gradient(ellipse at 20% 100%, rgba(77,224,255,.08), transparent 55%);
          isolation: isolate;
        }
        .info-hero::after {
          content: '';
          position: absolute; left: 0; right: 0; bottom: 0; height: 1px;
          background: linear-gradient(90deg, transparent, rgba(0,255,157,.4), transparent);
        }
        .blob { position: absolute; border-radius: 9999px; filter: blur(80px); opacity: .32; z-index: -1; animation: infoFloat 14s ease-in-out infinite; }
        .blob.b1 { width: 340px; height: 340px; background: #00ff9d; top: -80px; left: -80px; }
        .blob.b2 { width: 280px; height: 280px; background: #ff2d92; top: 30%; right: -60px; animation-delay: -4s; }
        .blob.b3 { width: 260px; height: 260px; background: #4de0ff; bottom: -60px; left: 40%; animation-delay: -8s; }
        @keyframes infoFloat {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(24px, -18px) scale(1.06); }
        }
        .grain {
          position: absolute; inset: 0; pointer-events: none; opacity: .07; z-index: -1;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
        }
        .scanlines {
          position: absolute; inset: 0; pointer-events: none; z-index: -1; opacity: .04;
          background-image: repeating-linear-gradient(0deg, transparent 0, transparent 2px, #00ff9d 3px, transparent 4px);
        }
        .grid-dots {
          position: absolute; inset: 0; pointer-events: none; z-index: -1; opacity: .07;
          background-image: radial-gradient(rgba(255,255,255,.35) 1px, transparent 1px);
          background-size: 22px 22px;
          mask-image: radial-gradient(ellipse at center, black 45%, transparent 80%);
        }
        .info-title {
          font-family: 'Cinzel', 'Playfair Display', serif;
          font-size: clamp(2.4rem, 8vw, 5rem);
          letter-spacing: -0.02em;
          background: linear-gradient(180deg, #ffffff 0%, #d5d5d5 55%, #7a7a7a 100%);
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent; color: transparent;
        }
        .cta-btn {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 11px 18px; border-radius: 8px;
          background: var(--c); color: #000;
          font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 0.18em; font-weight: 700;
          transition: transform .2s ease, filter .2s ease;
        }
        .cta-btn:hover { transform: translateY(-2px); filter: brightness(1.1); }
        .cta-btn.ghost {
          background: transparent; color: var(--c);
          border: 1px solid color-mix(in srgb, var(--c) 45%, transparent);
        }
        .cta-btn.ghost:hover { border-color: var(--c); background: color-mix(in srgb, var(--c) 8%, transparent); }

        /* BADGE STRIP */
        .info-badge {
          position: relative; padding: 18px 16px;
          border-radius: 14px; text-align: center;
          background: linear-gradient(160deg, color-mix(in srgb, var(--c) 10%, transparent), rgba(255,255,255,.02) 65%);
          border: 1px solid color-mix(in srgb, var(--c) 32%, transparent);
          transition: transform .25s ease, border-color .2s ease, box-shadow .3s ease;
          overflow: hidden;
        }
        .info-badge:hover {
          border-color: var(--c);
          transform: translateY(-3px);
          box-shadow: 0 16px 40px -14px color-mix(in srgb, var(--c) 60%, transparent);
        }
        .prism-bar {
          position: absolute; top: 0; left: 0; right: 0; height: 2px;
          background: linear-gradient(90deg, transparent, var(--c), transparent);
          opacity: .8;
        }
        .badge-icon {
          width: 42px; height: 42px; border-radius: 12px; margin: 0 auto 10px;
          display: grid; place-items: center;
          background: color-mix(in srgb, var(--c) 14%, transparent);
          border: 1px solid color-mix(in srgb, var(--c) 45%, transparent);
          color: var(--c);
        }
        .badge-label { font-family: 'Inter', sans-serif; font-weight: 700; font-size: 14px; margin-bottom: 4px; }
        .badge-desc { font-size: 10px; letter-spacing: 0.14em; opacity: .7; }

        /* PILLARS */
        .pillar-tile {
          position: relative; padding: 24px;
          border-radius: 16px;
          background: linear-gradient(160deg, color-mix(in srgb, var(--c) 8%, transparent), rgba(255,255,255,.02) 65%);
          border: 1px solid color-mix(in srgb, var(--c) 30%, transparent);
          overflow: hidden;
          transition: transform .3s ease, border-color .25s ease, box-shadow .3s ease;
        }
        .pillar-tile:hover {
          border-color: var(--c);
          transform: translateY(-4px);
          box-shadow: 0 22px 50px -16px color-mix(in srgb, var(--c) 55%, transparent);
        }
        .corner-glow {
          position: absolute; top: -40px; right: -40px; width: 140px; height: 140px;
          background: radial-gradient(circle at center, color-mix(in srgb, var(--c) 40%, transparent), transparent 70%);
          opacity: 0; transition: opacity .35s ease;
        }
        .pillar-tile:hover .corner-glow { opacity: 1; }
        .pillar-icon {
          width: 46px; height: 46px; border-radius: 12px; display: grid; place-items: center;
          background: color-mix(in srgb, var(--c) 14%, transparent);
          border: 1px solid color-mix(in srgb, var(--c) 45%, transparent);
          color: var(--c); margin-bottom: 14px;
        }
        .pillar-title { font-family: 'Inter', sans-serif; font-weight: 800; font-size: 18px; margin-bottom: 6px; }
        .pillar-body { font-family: 'Inter', sans-serif; font-size: 13px; opacity: .78; line-height: 1.6; }

        /* MEET THE TEAM CARD */
        .meet-team-card {
          position: relative; overflow: hidden;
          display: block; padding: 24px;
          border-radius: 18px;
          border: 1px solid rgba(0,255,157,.32);
          background: linear-gradient(135deg, rgba(0,255,157,.10), rgba(77,224,255,.06) 50%, rgba(255,45,146,.06));
          transition: transform .3s ease, box-shadow .3s ease, border-color .25s ease;
        }
        .meet-team-card:hover {
          transform: translateY(-3px);
          border-color: rgba(0,255,157,.6);
          box-shadow: 0 24px 60px -20px rgba(0,255,157,.35);
        }
        .prism-bar-rainbow {
          position: absolute; top: 0; left: 0; right: 0; height: 2.5px;
          background: linear-gradient(90deg, transparent, #00ff9d, #4de0ff, #ff2d92, #ffd34d, transparent);
        }
        .pulse-dot { position: absolute; top: 14px; right: 14px; display: inline-flex; width: 10px; height: 10px; }
        .pulse-ping { position: absolute; inset: 0; border-radius: 9999px; background: #00ff9d; opacity: .75; animation: infoPing 1.6s cubic-bezier(0,0,.2,1) infinite; }
        .pulse-core { position: relative; border-radius: 9999px; background: #00ff9d; width: 10px; height: 10px; box-shadow: 0 0 12px #00ff9d; }
        @keyframes infoPing { 75%,100% { transform: scale(2.2); opacity: 0; } }
        .team-avatar-tile {
          width: 54px; height: 54px; border-radius: 14px;
          background: rgba(0,255,157,.16); border: 1px solid rgba(0,255,157,.55); color: var(--eh-green);
          display: grid; place-items: center; flex-shrink: 0;
        }
        .team-cta {
          display: inline-flex; align-items: center; gap: 6px; padding: 10px 16px;
          background: var(--eh-green); color: #000;
          font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 0.18em; font-weight: 700;
          border-radius: 8px; flex-shrink: 0;
        }

        /* STAT CELL */
        .stat-cell {
          padding: 16px 18px; border-radius: 12px;
          background: linear-gradient(160deg, color-mix(in srgb, var(--c) 8%, transparent), transparent 70%);
          border: 1px solid color-mix(in srgb, var(--c) 30%, transparent);
          text-align: center;
          transition: transform .25s ease, border-color .25s ease;
        }
        .stat-cell:hover { transform: translateY(-3px); border-color: var(--c); }
        .stat-value { font-family: 'Cinzel', serif; font-weight: 900; font-size: clamp(1.6rem, 4vw, 2.4rem); line-height: 1; }
        .stat-label { font-size: 10px; letter-spacing: 0.25em; opacity: .7; margin-top: 8px; }

        /* CONTACT CARD */
        .contact-card {
          position: relative; padding: 40px 28px;
          border-radius: 20px; border: 1px solid rgba(0,255,157,.28);
          background: linear-gradient(160deg, rgba(0,255,157,.06), rgba(77,224,255,.03) 60%, transparent);
          overflow: hidden;
        }

        @media (prefers-reduced-motion: reduce) {
          .blob, .pulse-ping { animation: none !important; }
        }
      `}</style>
    </div>
  );
};

export default InfoPage;
